import { useEffect } from 'react';
import PageLayout from '../../components/base/PageLayout';
import content from './book-content.json';
import metadata from '../../seo/seo-metadata.json';
import { BOOK_ROUTE, SITE_ORIGIN, bookSchema } from './book-schema';

export default function BookPage({ lang = 'ja' }: { lang?: 'ja' | 'en' }) {
  const isEnglish = lang === 'en';
  const copy = content[lang];
  const route = `${isEnglish ? '/en' : ''}${BOOK_ROUTE}`;
  const home = isEnglish ? '/en' : '/';
  const crumb = isEnglish ? copy.H1 : `『${copy.H1}』`;

  useEffect(() => {
    document.title = copy.TITLE;
    document.documentElement.lang = lang;
    const meta = (attribute: 'name' | 'property', key: string, value: string) => {
      let node = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.content = value;
    };
    meta('name', 'description', copy.DESCRIPTION);
    meta('name', 'keywords', metadata.find((entry) => entry.route === route)?.keywords.join(', ') ?? '');
    const canonical = `${SITE_ORIGIN}${route}`;
    const image = `${SITE_ORIGIN}/images/book/bell-book-billboard-20260915.jpg`;
    for (const [key, value] of Object.entries({
      'og:type': 'book',
      'og:title': copy.TITLE,
      'og:description': copy.DESCRIPTION,
      'og:image': image,
      'og:url': canonical,
      'og:locale': isEnglish ? 'en_US' : 'ja_JP',
    })) meta('property', key, value);
    for (const [key, value] of Object.entries({
      'twitter:card': 'summary_large_image',
      'twitter:title': copy.TITLE,
      'twitter:description': copy.DESCRIPTION,
      'twitter:image': image,
    })) meta('name', key, value);
    document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    const links = [
      { rel: 'canonical', href: canonical },
      ...[['ja', BOOK_ROUTE], ['en', `/en${BOOK_ROUTE}`], ['x-default', BOOK_ROUTE]].map(([hreflang, path]) => ({
        rel: 'alternate', hreflang, href: `${SITE_ORIGIN}${path}`,
      })),
    ].map((attributes) => {
      const node = document.createElement('link');
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
      document.head.appendChild(node);
      return node;
    });
    const schemas = [bookSchema(route), {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: isEnglish ? 'Home' : 'ホーム', item: `${SITE_ORIGIN}${home}` },
        { '@type': 'ListItem', position: 2, name: crumb, item: canonical },
      ],
    }].map((schema) => {
      const node = document.createElement('script');
      node.type = 'application/ld+json';
      node.dataset.pageSchema = 'book';
      node.textContent = JSON.stringify(schema);
      document.head.appendChild(node);
      return node;
    });
    return () => {
      schemas.forEach((node) => node.remove());
      links.forEach((node) => node.remove());
      document.documentElement.lang = 'ja';
    };
  }, [copy, crumb, home, isEnglish, lang, route]);

  return (
    <PageLayout
      breadcrumbItems={[
        { name: isEnglish ? 'Home' : 'ホーム', href: home },
        { name: crumb },
      ]}
      hero={<>
        <section className="relative py-16 md:py-32 min-h-[420px] md:min-h-[560px] overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
          <img
            src="/images/book/bell-book-billboard-20260915.jpg"
            alt={copy.BILLBOARD_ALT}
            className="absolute inset-0 h-full w-full object-cover object-center opacity-30"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-amber-400/20 text-amber-300 px-5 sm:px-6 lg:px-8 py-2 rounded-full text-sm font-medium">
                  {copy.KICKER}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-serif text-white mb-6">{copy.H1}</h1>
            </div>
          </div>
        </section>
      </>}
    >
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <article className="py-20" lang={lang}>
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">{copy.LEAD}</p>
              </div>

              {copy.sections.map((section, sectionIndex) => (
                <section className="mb-12" key={section.heading}>
                  <h2 className="text-3xl font-serif text-gray-900 mb-6">{section.heading}</h2>
                  <div className={sectionIndex === 0 ? 'bg-gray-50 p-6 rounded-lg mb-8' : ''}>
                    <img
                      src={section.image}
                      alt={section.alt}
                      className={`w-full h-auto ${sectionIndex === 0 ? 'max-w-sm mx-auto object-contain' : 'aspect-[16/9] object-cover object-center'} rounded-lg shadow-lg mb-8`}
                      loading="lazy"
                    />
                  </div>
                  {section.paragraphs.map((paragraph) => (
                    <p className="text-gray-700 leading-relaxed mb-6" key={paragraph}>{paragraph}</p>
                  ))}

                  {sectionIndex === 0 && (
                    <>
                      <div className="overflow-x-auto mb-8">
                        <table className="w-full bg-white border border-gray-200">
                          <tbody>
                            {copy.biblio.map(([label, value]) => (
                              <tr className="border-b border-gray-200" key={label}>
                                <th className="text-left text-amber-800 font-medium p-4 bg-amber-50 whitespace-nowrap" scope="row">{label}</th>
                                <td className="text-gray-700 p-4">{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-center mb-8">
                        <a
                          href="https://www.amazon.co.jp/dp/4058027606"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-gold inline-block bg-amber-700 text-white px-6 md:px-10 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-800 transition-colors whitespace-nowrap text-sm md:text-base"
                          aria-label={copy.AMAZON_ARIA}
                        >
                          {copy.CTA}<i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                        </a>
                      </div>
                    </>
                  )}

                  {section.subsections.map((subsection) => (
                    <div key={subsection.heading}>
                      <h3 className="text-2xl font-serif text-gray-800 mb-4">{subsection.heading}</h3>
                      {subsection.paragraphs.map((paragraph) => (
                        <p className="text-gray-700 leading-relaxed mb-6" key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          </div>
        </article>
      </div>
    </PageLayout>
  );
}
